import React, { useEffect, useMemo, useState } from 'react';
import { Spinner, SpinnerSize } from '@fluentui/react';

import * as SyncService from '../../../../services/ProtocolConsumptionSyncProcess.Service';
import * as AnalysisService from '../../../../services/ProtocolConsumptionSyncAnalysis.Service';
import { ProtocolConsumptionSyncProcess } from '../../../../models/Entities/Protocols/ProtocolConsumptionSyncProcess';
import { SyncAnalysis } from '../../../../models/Entities/Protocols/SyncAnalysis';
import { SyncAnalysisFindingTypeEnum } from '../../../../models/Enums/SyncAnalysisFindingTypeEnum';
import { SyncAnalysisStatusEnum } from '../../../../models/Enums/SyncAnalysisStatusEnum';
import {
    FINDING_TYPES,
    getStatusLabel
} from '../../../Protocols/ConsumptionSyncProcess/ConsumptionsSyncProcessIAAnalisys/ConsumptionSyncProcessIAAnalysis.Functions';
import SyncAnalysisSummaryComponent from '../../../Protocols/ConsumptionSyncProcess/ConsumptionsSyncProcessIAAnalisys/SyncAnalysisSummary.Component';

interface IProps {
    ProtocolId: number;
    AccessToken: string | undefined;
}

/** Códigos de estado de la corrida, tal como los devuelve el backend. */
const RUNNING_STATUSES = [1, 2];
const FINISHED_STATUS = 3;
const ERROR_STATUS = 5;

const formatDateTime = (value?: Date | string | null): string => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

/** Días desde la última corrida: una sincronización vieja explica consumos faltantes. */
const daysSince = (value?: Date | string | null): number | undefined => {
    if (!value) return undefined;
    const time = new Date(value).getTime();
    if (Number.isNaN(time)) return undefined;
    return Math.floor((Date.now() - time) / 86400000);
};

/**
 * Los contadores por tipo llegan con la clave que arma el servidor: puede ser el
 * número del enum ("1") o su nombre ("VisitWithoutConsumption"). Se contemplan las
 * dos formas, porque leer mal esto es mostrar cero hallazgos donde hay diez.
 */
const countFor = (counts: Record<string, number> | null | undefined, type: SyncAnalysisFindingTypeEnum): number => {
    if (!counts) return 0;
    const byNumber = counts[String(type)];
    const byName = counts[SyncAnalysisFindingTypeEnum[type]];
    return byNumber ?? byName ?? 0;
};

/**
 * Estado de la sincronización y del análisis automático.
 *
 * Va al final del panel porque no es una cifra del contrato: es la explicación de
 * por qué pueden faltar consumos. Si la última corrida falló, quedó a medias o es
 * vieja, todo lo que está más arriba puede estar incompleto, y el administrativo
 * tiene que saberlo antes de liquidar.
 */
export const SyncHealthComponent: React.FC<IProps> = ({ ProtocolId, AccessToken }) => {
    const [process, setProcess] = useState<ProtocolConsumptionSyncProcess | undefined>(undefined);
    const [analysis, setAnalysis] = useState<SyncAnalysis | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [failed, setFailed] = useState<boolean>(false);

    useEffect(() => {
        if (!AccessToken || !ProtocolId) return;
        let cancelled = false;
        setIsLoading(true);
        setFailed(false);

        SyncService.GetLastProtocolConsumptionSyncProcess(ProtocolId, AccessToken)
            .then(async (last) => {
                if (cancelled) return;
                setProcess(last);
                if (!last?.id) return;
                // Sin análisis es un estado normal: el servicio devuelve null, no error.
                const result = await AnalysisService.GetProtocolConsumptionSyncAnalysis(last.id, AccessToken).catch(() => null);
                if (!cancelled) setAnalysis(result);
            })
            .catch(() => {
                if (!cancelled) setFailed(true);
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [ProtocolId, AccessToken]);

    const status = process?.status;
    const isRunning = status !== undefined && RUNNING_STATUSES.includes(status);
    const age = daysSince(process?.lastSync);

    const syncPill = useMemo(() => {
        if (isRunning) return { className: 'bc-pill bc-pill--warn', label: 'En curso' };
        if (status === ERROR_STATUS) return { className: 'bc-pill bc-pill--bad', label: 'Falló' };
        if (status === FINISHED_STATUS) return { className: 'bc-pill bc-pill--ok', label: 'Terminó bien' };
        return { className: 'bc-pill bc-pill--idle', label: 'Sin corridas' };
    }, [status, isRunning]);

    const findings = FINDING_TYPES.map((descriptor) => ({
        ...descriptor,
        count: countFor(analysis?.findingCountsByType, descriptor.type)
    }));
    const totalFindings = findings.reduce((acc, item) => acc + item.count, 0);

    if (isLoading && !process) {
        return (
            <div className="cs-card">
                <div className="cs-loading cs-loading--compact">
                    <Spinner size={SpinnerSize.small} />
                    <span>Buscando la última sincronización…</span>
                </div>
            </div>
        );
    }

    return (
        <div className="cs-card">
            <div className="cs-card__header">
                <div className="cs-card__title">Sincronización y revisión automática</div>
                <div className="cs-card__desc">
                    De acá salen los consumos: el proceso recorre el registro clínico contra el contrato y los genera. Si
                    esta corrida falló o es vieja, lo de arriba puede estar incompleto.
                </div>
            </div>

            {failed && (
                <div className="cs-cf-error" role="alert">
                    <strong>No se pudo consultar el estado de la sincronización.</strong> Los consumos que se ven arriba
                    siguen siendo válidos, pero no se puede saber si están actualizados.
                </div>
            )}

            <div className="cs-card__body">
                <div className="bc-sync">
                    <div className="bc-sync__item">
                        <div className="bc-sync__label">Última corrida</div>
                        <div className="bc-sync__value">
                            <span className={syncPill.className}>{syncPill.label}</span>
                        </div>
                        <div className="bc-sync__desc">
                            {formatDateTime(process?.lastSync)}
                            {age !== undefined && age > 7 && (
                                <> · hace {age} días</>
                            )}
                        </div>
                    </div>

                    <div className="bc-sync__item">
                        <div className="bc-sync__label">Definiciones de facturables</div>
                        <div className="bc-sync__value">
                            {process?.useMedicalPayableDefinition === false ? 'No usadas' : 'Usadas'}
                        </div>
                        <div className="bc-sync__desc">
                            {process?.useMedicalPayableDefinition === false
                                ? 'La corrida ignoró qué ítems son facturables: puede haber generado de más o de menos'
                                : 'La corrida respetó qué ítems del registro clínico son facturables'}
                        </div>
                    </div>

                    <div className="bc-sync__item">
                        <div className="bc-sync__label">Revisión automática</div>
                        <div className="bc-sync__value">
                            {analysis ? getStatusLabel(analysis.status) : 'Sin analizar'}
                        </div>
                        <div className="bc-sync__desc">
                            {analysis?.status === SyncAnalysisStatusEnum.SkippedNoIndicators
                                ? 'La corrida no dejó indicios para analizar'
                                : analysis
                                    ? `${analysis.evidenceItemCount} elementos revisados · ${analysis.pendingFindingCount} pendientes`
                                    : 'Esta corrida todavía no se analizó'}
                        </div>
                    </div>

                    <div className="bc-sync__item">
                        <div className="bc-sync__label">Hallazgos</div>
                        <div className="bc-sync__value">{totalFindings}</div>
                        <div className="bc-sync__desc">
                            {totalFindings > 0
                                ? 'Cosas que la revisión marcó como posibles faltantes'
                                : 'La revisión no marcó faltantes'}
                        </div>
                    </div>
                </div>

                {process?.status === ERROR_STATUS && process.errorMessage && (
                    <div className="cs-cf-error" role="alert">
                        <strong>La última sincronización falló.</strong> {process.errorMessage}
                    </div>
                )}

                {analysis?.summary && (
                    <div className="cs-block--divided">
                        <div className="cs-cf-chart-title">Qué encontró la revisión</div>
                        <SyncAnalysisSummaryComponent summary={analysis.summary} />
                    </div>
                )}

                {totalFindings > 0 && (
                    <div className="cs-table-wrap cs-block--divided">
                        <table className="cs-table">
                            <thead>
                                <tr>
                                    <th>Tipo de hallazgo</th>
                                    <th>Qué mira</th>
                                    <th className="cs-table__amount">Casos</th>
                                </tr>
                            </thead>
                            <tbody>
                                {findings
                                    .filter((item) => item.count > 0)
                                    .map((item) => (
                                        <tr key={item.type}>
                                            <td><strong>{item.label}</strong></td>
                                            <td>{item.description}</td>
                                            <td className="cs-table__amount">{item.count}</td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                    </div>
                )}

                <div className="fp-source">
                    <span>
                        <strong>Cómo leer esto.</strong> Los hallazgos los produce la revisión automática sobre la última
                        corrida, y el detalle de cada uno —con su evidencia y su recomendación— se ve en la pantalla de
                        consumos del protocolo. Una corrida vieja o que falló explica la mayoría de los faltantes de
                        arriba: conviene volver a sincronizar antes de liquidar.
                    </span>
                </div>
            </div>
        </div>
    );
};

export default SyncHealthComponent;
